import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  Chip,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { AdminPanelSettings } from '@mui/icons-material';
import { useAuth } from './AuthContext';
import { API_URL } from './constants';
import axios from 'axios';

const ROLE_OPTIONS = ['User', 'Power User', 'Admin'];

const RoleManagementView = () => {
  const { isSuperAdmin, userProjectKey } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleSavingId, setRoleSavingId] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const canManageRoles = isSuperAdmin && userProjectKey === 'admin';

  useEffect(() => {
    if (!canManageRoles) {
      setUsers([]);
      return;
    }

    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/auth/admin/role-management/users`, {
          withCredentials: true,
        });
        setUsers(response.data?.users || []);
      } catch (error) {
        setMessage(error.response?.data?.detail || 'Failed to load users for role management.');
        setMessageType('error');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [canManageRoles]);

  const handleRoleChange = async (targetUserId, nextRole) => {
    if (!canManageRoles) return;
    setRoleSavingId(targetUserId);
    try {
      await axios.put(
        `${API_URL}/api/auth/admin/users/${targetUserId}/role`,
        { role: nextRole },
        { withCredentials: true }
      );
      setUsers((prev) =>
        prev.map((u) => (u.id === targetUserId ? { ...u, role: nextRole } : u))
      );
      setMessage('User role updated successfully.');
      setMessageType('success');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to update user role.');
      setMessageType('error');
    } finally {
      setRoleSavingId(null);
    }
  };

  if (!canManageRoles) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error">
          Role management is only available to the platform superadmin within the admin project.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto', mb: 4 }}>
      <Card sx={{ border: '2px solid', borderColor: 'error.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AdminPanelSettings color="error" />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Platform Role Management
            </Typography>
            <Chip label="Superadmin Only" color="error" size="small" sx={{ ml: 'auto' }} />
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Manage roles for all users across the platform. Roles: <strong>User</strong>,{' '}
              <strong>Power User</strong>, and <strong>Admin</strong>. New accounts default to{' '}
              <strong>User</strong>.
            </Typography>
          </Alert>

          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Loading users...
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {users.map((u) => (
                <Grid item xs={12} key={u.id}>
                  <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Typography variant="subtitle2">{u.name || u.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {u.email}
                        </Typography>
                        {u.project_key && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Project: {u.project_key}
                          </Typography>
                        )}
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={u.role || 'User'}
                          label="Role"
                          onChange={(event) => handleRoleChange(u.id, event.target.value)}
                          disabled={roleSavingId === u.id}
                        >
                          {ROLE_OPTIONS.map((roleOption) => (
                            <MenuItem key={roleOption} value={roleOption}>
                              {roleOption}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!message}
        autoHideDuration={6000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setMessage('')} severity={messageType} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleManagementView;
